import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { StyledContainer } from '../styles/hatm.styles';
import { HatmProvider } from '../context/HatmContext';
import { useHatmData } from '../hooks/useHatmData';
import LoadingScreen from '../components/LoadingScreen';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useHatm } from '../context/HatmContext';
import Joyride from 'react-joyride';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';


// Components
import HatmHeader from '../components/Hatm/HatmHeader';
import HatmProgress from '../components/Hatm/HatmProgress';
import HatmGrid from '../components/Hatm/HatmGrid';
import HatmFooter from '../components/Hatm/HatmFooter';
import AlertSystem from '../components/Hatm/AlertSystem';
import QRDialog from '../components/Hatm/QRDialog';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#F7E1D7',
    borderRadius: '16px',
    padding: theme.spacing(4),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  color: '#333A3B',
  fontWeight: 'bold',
}));

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
  color: '#555',
  fontSize: '1rem',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  padding: '10px 20px',
  fontWeight: 'bold',
  transition: 'background-color 0.3s, transform 0.2s',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',

  // Birincil Buton Stilleri
  '&.primary': {
    background: 'linear-gradient(45deg, #FF6B81 30%, #FF8DAA 90%)', // Pembe gradient
    color: '#fff',
    '&:hover': {
      background: 'linear-gradient(45deg, #FF8DAA 30%, #FF6B81 90%)',
      transform: 'scale(1.05)',
    },
  },

  // İkincil Buton Stilleri
  '&.secondary': {
    background: '#ffe4e6', // Açık pembe
    color: '#333',
    '&:hover': {
      background: '#ffccd1',
      transform: 'scale(1.05)',
    },
  },

  // Odaklanma Durumu
  '&:focus': {
    outline: '2px solid #FF8DAA',
    outlineOffset: '2px',
  },

  // Responsive Tasarım
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    fontSize: '0.9rem',
  },
}));


export default function HatmPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  // Get hatmId from URL
  const hatmId = window.location.pathname.split("/").pop();

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Handle loading state
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <HatmProvider>
      <HatmPageContent 
        hatmId={hatmId} 
        auth={auth}
        isAuthenticated={isAuthenticated}
      />
    </HatmProvider>
  );
}

function HatmPageContent({ hatmId, auth, isAuthenticated }) {
  // Update context usage
  const { state, setAlert, setAlertMessage } = useHatm();
  const [runTour, setRunTour] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const hasTour = localStorage.getItem('hatmTour');
    if (!hasTour) {
      setOpenDialog(true);
    }
  }, []);

  const handleDialogClose = () => {
    setOpenDialog(false);
    localStorage.setItem('hatmTour', 'skipped');
  };

  const handleStartTour = () => {
    setRunTour(true);
    setOpenDialog(false);
    localStorage.setItem('hatmTour', 'started');
  };

  const handleTourCallback = (data) => {
    const { status } = data;
    if (['finished', 'skipped'].includes(status)) {
      setRunTour(false);
    }
  };

  const steps = [
    {
      target: '.hatm-header-section',
      content: 'Bu uygulama, hatimleriniz için cüz seçmenizi ve isim girmenizi sağlar.',
      disableBeacon: true,
    },
    {
      target: '.hatm-progress',
      content: 'Hatmin genel ilerlemesini burada görebilirsiniz.',
    },
    {
      target: '.hatm-grid',
      content: 'Tüm cüzleri ve okuyucuları burada görebilirsiniz.',
    },
    {
      target: '.hatm-first-incomplete-juz',
      content: 'Buradan okuyacağınız cüzleri seçin.',
    },
    {
      target: '.hatm-footer-name',
      content: 'İsim ve soyisminizi buraya girin.',
    },
    {
      target: '.hatm-footer-submit',
      content: 'Seçimlerinizi tamamladıktan sonra gönder butonuna tıklayın.',
    },
  ];

  // Use custom hook to fetch and manage hatm data
  useHatmData(hatmId);

  const handleRemoveParticipant = async (juzNumber) => {
    if (!state || !state.juzs || !auth.currentUser || auth.currentUser.uid !== state.creator) return;

    try {
        const hatmRef = doc(db, "hatms", hatmId);
        const newJuzs = { ...state.juzs };
        
        if (newJuzs[juzNumber]) {
            const participantData = newJuzs[juzNumber];
            delete newJuzs[juzNumber];

            await updateDoc(hatmRef, {
                juzs: newJuzs
            });

            if (participantData && participantData.userId) {
                const userRef = doc(db, "users", participantData.userId);
                await updateDoc(userRef, {
                    [`hatms.${hatmId}.${juzNumber}`]: deleteField()
                });
            }
        }
    } catch (error) {
        console.error("Error removing participant:", error);
        setAlertMessage("Katılımcı kaldırılırken bir hata oluştu!");
        setAlert(true);
    }
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        callback={handleTourCallback}
        locale={{
          back: 'Geri',
          close: 'Kapat',
          last: 'Son',
          next: 'İleri',
          skip: 'Atla',
        }}
        styles={{
          options: {
            zIndex: 10000,
          },
        }}
      />

      <StyledDialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="tour-dialog-title"
        aria-describedby="tour-dialog-description"
      >
        <StyledDialogTitle id="tour-dialog-title">Tur Başlatılsın Mı?</StyledDialogTitle>
        <DialogContent>
          <StyledDialogContentText id="tour-dialog-description">
            Uygulamamızı daha iyi anlamak için bir tur başlatmak ister misiniz?
          </StyledDialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton
            onClick={handleDialogClose}
            className="secondary"
            variant="outlined"
            aria-label="Hayır"
          >
            Hayır
          </StyledButton>
          <StyledButton
            onClick={handleStartTour}
            className="primary"
            variant="contained"
            autoFocus
            aria-label="Evet"
          >
            Evet
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      <HatmProgress />
      
      <StyledContainer>
        <HatmHeader hatmId={hatmId} />

        <HatmGrid 
          hatmId={hatmId}
          auth={auth}
          onRemoveParticipant={handleRemoveParticipant}
        />

        <HatmFooter hatmId={hatmId} />

        <AlertSystem />
        
        <QRDialog hatmId={hatmId} />
      </StyledContainer>
    </>
  );
}
